import { i18next } from '@/shared/lib/i18n';

type RequestFailureListener = (error: HttpError | NetworkError | TimeoutError) => void;
let requestFailureListener: RequestFailureListener | undefined;

/**
 * Кому сообщать о каждой неудаче запроса — подставляет `main.tsx` (отчёт в
 * служебный чат, `shared/lib/reportError.ts`). Зовётся из конструкторов
 * ошибок ниже, а не из `apiRequest`: мимо него ходят голос, поток хода
 * диалога и оценка уровня со своими `fetch`, а ошибку они создают те же.
 * После конструктора (`queueMicrotask`) — объект уже собран целиком.
 */
export function setRequestFailureListener(listener: RequestFailureListener): void {
  requestFailureListener = listener;
}

function failed(error: HttpError | NetworkError | TimeoutError): void {
  queueMicrotask(() => requestFailureListener?.(error));
}

/** Сервер ответил, но со статусом ошибки. */
export class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly url: string,
    readonly body: string,
  ) {
    super(`${url} → ${status}`);
    this.name = 'HttpError';
    failed(this);
  }
}

/**
 * До ответа дело не дошло: сервер недоступен, смешанный контент http/https,
 * CORS, оборвался тоннель. Статус-кода здесь нет, поэтому в сообщении держим
 * адрес — иначе в UI видно только голое "Load failed".
 */
export class NetworkError extends Error {
  /** `quiet` — фоновый запрос, сбой которого человек не видит: в отчёт не идёт. */
  constructor(
    readonly url: string,
    override readonly cause: unknown,
    quiet = false,
  ) {
    const reason = cause instanceof Error ? cause.message : String(cause);
    super(i18next.t('networkError', { ns: 'errors', url, reason }));
    this.name = 'NetworkError';
    if (!quiet) failed(this);
  }
}

/** Запрос отменён (размонтирование компонента или таймаут). */
export class TimeoutError extends Error {
  constructor(
    readonly url: string,
    quiet = false,
  ) {
    super(i18next.t('timeoutError', { ns: 'errors', url }));
    this.name = 'TimeoutError';
    if (!quiet) failed(this);
  }
}

/**
 * Идут технические работы (`MAINTENANCE_MODE` на бэкенде), и этого человека
 * нет в `ALLOWED_TELEGRAM_IDS` — вместо приложения «Ведутся технические работы».
 */
export function isMaintenanceError(error: unknown): boolean {
  return error instanceof HttpError && error.status === 503 && error.body.includes('maintenance');
}

/** Событие на `window`: любой запрос упёрся в технические работы (см. `main.tsx`). */
export const MAINTENANCE_EVENT = 'app:maintenance';

/** Гость веб-версии на маршруте, открытом только после входа (403 `login_required`). */
export function isLoginRequiredError(error: unknown): boolean {
  return error instanceof HttpError && error.status === 403 && error.body.includes('login_required');
}

/** Событие на `window`: запрос гостя упёрся в `login_required` (см. `AppRouter`). */
export const LOGIN_REQUIRED_EVENT = 'app:login-required';

export function toUserMessage(error: unknown): string {
  if (error instanceof HttpError) {
    return error.status === 401
      ? i18next.t('authError', { ns: 'errors' })
      : i18next.t('serverError', { ns: 'errors', status: error.status });
  }
  if (error instanceof NetworkError || error instanceof TimeoutError) return error.message;
  return error instanceof Error ? error.message : String(error);
}
